import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Container de todo conjunto de dados: uma tabela, um agrupamento por dia, uma
 * quinzena. Página em `bg-background`, dados em card `bg-card` — nunca o
 * contrário.
 *
 * `aviso` é a faixa de atenção que a Irrigação já usava à mão ("As estações
 * somam 29% mais água do que o hidrômetro mediu"), com cor fixa escrita na
 * página. Aqui é slot, no tom `atencao` do sistema.
 *
 * Uma sombra só (`shadow-xs`): profundidade vem de borda e fundo, não de
 * sombras empilhadas.
 */
export interface DataCardProps {
  /** Cabeçalho do grupo: "sex 21/08", "Quinzena 16–31/08". */
  titulo?: string;
  /** Metadado do grupo: "840,5 m³ medidos · 14 setores". */
  meta?: React.ReactNode;
  /** StatusBadge do grupo. Um badge por eixo — nunca funda dois estados. */
  estado?: React.ReactNode;
  /** Ação à direita do cabeçalho, normalmente um botão de ícone. */
  acao?: React.ReactNode;
  /** Faixa de atenção logo abaixo do cabeçalho. */
  aviso?: React.ReactNode;
  /** Totais e contagem, no pé do card. */
  rodape?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** `false` quando o filho já traz o próprio espaçamento (tabela, por ex.). */
  padded?: boolean;
  testId?: string;
}

export function DataCard({
  titulo,
  meta,
  estado,
  acao,
  aviso,
  rodape,
  children,
  className,
  padded = false,
  testId,
}: DataCardProps) {
  const temCabecalho = Boolean(titulo || meta || estado || acao);

  return (
    <div
      className={cn("rounded-lg border bg-card shadow-xs overflow-hidden", className)}
      data-testid={testId}
    >
      {temCabecalho && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-3 py-2">
          <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
            {titulo && <span className="text-sm font-semibold truncate">{titulo}</span>}
            {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {estado}
            {acao}
          </div>
        </div>
      )}

      {aviso && (
        <div className="border-b bg-atencao-suave px-3 py-2 text-xs text-atencao">
          {aviso}
        </div>
      )}

      <div className={cn(padded && "p-3")}>{children}</div>

      {rodape && (
        <div className="border-t bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {rodape}
        </div>
      )}
    </div>
  );
}
